import { useCallback, useEffect, useMemo, useState } from "react";
type OrderRow = {
  restaurant_id: string;
  order_id: string;
  order_number: number | string;
  order_type: string | null;
  table_id: string | null;
  notes: string | null;
  status: string;
  total_minor: number;
  amount_paid_minor: number;
  balance_minor: number;
  submitted_at: string | null;
  updated_at: string;
  source?: "online" | "projected";
};

type ItemRow = {
  restaurant_id: string;
  order_id: string;
  order_item_id: string;
  name: string;
  quantity: number;
  line_total_minor: number;
  notes: string | null;
};

function money(minor: number) {
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
  }).format(minor / 100);
}

export function NovaPosBoard() {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const [
    updatingOrderId,
    setUpdatingOrderId,
  ] = useState<string | null>(
    null,
  );

  const [
    actionError,
    setActionError,
  ] = useState<string | null>(
    null,
  );

  const load = useCallback(async () => {
    try {
      const response = await fetch("/api/pos/orders", {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      const body = await response.json();

      if (response.status === 401) {
        window.location.reload();
        return;
      }

      if (!response.ok) {
        throw new Error(body.error || `Request failed (${response.status})`);
      }

      setOrders(body.orders ?? []);
      setItems(body.items ?? []);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to load Web POS orders",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOrderStatus =
    useCallback(
      async (
        orderId: string,
        status:
          | "accepted"
          | "preparing"
          | "ready"
          | "completed"
          | "cancelled",
      ) => {
        if (updatingOrderId) {
          return;
        }

        setUpdatingOrderId(
          orderId,
        );

        setActionError(
          null,
        );

        try {
          const response =
            await fetch(
              "/api/pos/orders/status",
              {
                method:
                  "POST",

                credentials:
                  "include",

                headers: {
                  "content-type":
                    "application/json",

                  Accept:
                    "application/json",
                },

                body:
                  JSON.stringify({
                    orderId,
                    status,
                  }),
              },
            );

          const body =
            await response.json();

          if (
            response.status ===
            401
          ) {
            window.location.reload();
            return;
          }

          if (!response.ok) {
            throw new Error(
              body.error ||
                `Unable to update order (${response.status})`,
            );
          }

          await load();
        } catch (error) {
          setActionError(
            error instanceof Error
              ? error.message
              : "Unable to update order.",
          );
        } finally {
          setUpdatingOrderId(
            null,
          );
        }
      },
      [
        load,
        updatingOrderId,
      ],
    );

  useEffect(() => {
    void load();

    const refreshIfVisible = () => {
      if (
        document.visibilityState ===
        "visible"
      ) {
        void load();
      }
    };

    const interval =
      window.setInterval(
        refreshIfVisible,
        20000,
      );

    document.addEventListener(
      "visibilitychange",
      refreshIfVisible,
    );

    window.addEventListener(
      "focus",
      refreshIfVisible,
    );

    return () => {
      window.clearInterval(
        interval,
      );

      document.removeEventListener(
        "visibilitychange",
        refreshIfVisible,
      );

      window.removeEventListener(
        "focus",
        refreshIfVisible,
      );
    };
  }, [load]);

  const itemsByOrder = useMemo(() => {
    const grouped = new Map<string, ItemRow[]>();

    for (const item of items) {
      const existing = grouped.get(item.order_id) ?? [];
      existing.push(item);
      grouped.set(item.order_id, existing);
    }

    return grouped;
  }, [items]);

  if (loading) {
    return (
      <div className="py-16 text-center text-[#CFC5B2]">
        Loading live orders...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-400/20 bg-red-500/10 p-5 text-red-200">
        <p className="font-semibold">Unable to load Web POS orders</p>
        <p className="mt-2 text-sm">{error}</p>

        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 rounded-lg border border-red-300/30 px-4 py-2 text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <section>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-[#C5A059]">
            Live Operations
          </p>
          <h2 className="mt-1 text-2xl font-semibold">
            Order Board
          </h2>
          <p className="mt-1 text-sm text-[#AFA695]">
            {orders.length} cloud-projected orders
          </p>
        </div>

        <div className="text-right text-xs text-[#8E877B]">
          <p>Auto refresh: 20 seconds while visible</p>
          <p>
            {lastRefresh
              ? `Updated ${lastRefresh.toLocaleTimeString()}`
              : "Waiting for first refresh"}
          </p>
        </div>
      </div>

      {actionError && (
        <div className="mb-5 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {actionError}
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        {[
          [
            "New online",
            orders.filter(
              (order) =>
                order.source ===
                  "online" &&
                order.status ===
                  "pending",
            ).length,
          ],
          [
            "Preparing",
            orders.filter(
              (order) =>
                order.status ===
                "preparing",
            ).length,
          ],
          [
            "Ready",
            orders.filter(
              (order) =>
                order.status ===
                "ready",
            ).length,
          ],
          [
            "Active",
            orders.filter(
              (order) =>
                ![
                  "completed",
                  "cancelled",
                ].includes(
                  order.status,
                ),
            ).length,
          ],
        ].map(
          ([label, value]) => (
            <div
              key={String(label)}
              className="rounded-xl border border-white/10 bg-[#191713] px-4 py-3"
            >
              <p className="text-[10px] font-bold uppercase tracking-[.15em] text-[#8E877B]">
                {label}
              </p>

              <p className="mt-1 text-2xl font-black text-[#F3E8CF]">
                {value}
              </p>
            </div>
          ),
        )}
      </div>

      {orders.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#191713] p-10 text-center">
          <p className="text-lg font-medium">No cloud orders yet</p>
          <p className="mt-2 text-sm text-[#AFA695]">
            Edge orders will appear here after synchronization.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {orders.map((order) => {
            const orderItems = itemsByOrder.get(order.order_id) ?? [];

            return (
              <article
                key={order.order_id}
                className={[
                  "rounded-2xl border p-5 transition",
                  order.source === "online" &&
                  order.status === "pending"
                    ? "border-[#F5C242]/60 bg-[#211D13] shadow-[0_0_0_1px_rgba(245,194,66,.12)]"
                    : "border-white/10 bg-[#191713]",
                ].join(" ")}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      {order.source === "online" && (
                        <span className="rounded-full bg-[#F5C242] px-2.5 py-1 text-[10px] font-black uppercase tracking-[.14em] text-[#231D0E]">
                          {order.status === "pending"
                            ? "New online order"
                            : "Online"}
                        </span>
                      )}

                      <h3 className="text-xl font-semibold">
                        Order #{order.order_number}
                      </h3>

                      <span className="rounded-full border border-[#C5A059]/30 bg-[#C5A059]/10 px-3 py-1 text-xs font-medium text-[#D9BA73]">
                        {order.status}
                      </span>
                    </div>

                    <p className="mt-1 text-sm text-[#AFA695]">
                      {order.order_type ?? "ORDER"}
                      {order.table_id
                        ? ` · Table ${order.table_id}`
                        : ""}
                    </p>
                    {order.notes && (
                      <p className="mt-2 text-xs leading-5 text-white/60">
                        {order.notes}
                      </p>
                    )}
                  </div>

                  <div className="text-right">
                    <p className="text-xl font-semibold">
                      {money(order.total_minor)}
                    </p>

                    {order.balance_minor > 0 ? (
                      <p className="text-xs text-[#D9BA73]">
                        Balance {money(order.balance_minor)}
                      </p>
                    ) : null}
                  </div>
                </div>

                {order.source === "online" &&
                  ![
                    "completed",
                    "cancelled",
                  ].includes(
                    order.status,
                  ) && (
                    <div className="mt-5 flex flex-wrap gap-2 border-t border-white/10 pt-4">
                      {order.status ===
                        "pending" && (
                        <button
                          type="button"
                          disabled={
                            updatingOrderId ===
                            order.order_id
                          }
                          onClick={() =>
                            void updateOrderStatus(
                              order.order_id,
                              "accepted",
                            )
                          }
                          className="rounded-lg bg-[#F5C242] px-4 py-2 text-xs font-black uppercase tracking-[.08em] text-[#231D0E] disabled:opacity-50"
                        >
                          Accept order
                        </button>
                      )}

                      {order.status ===
                        "accepted" && (
                        <button
                          type="button"
                          disabled={
                            updatingOrderId ===
                            order.order_id
                          }
                          onClick={() =>
                            void updateOrderStatus(
                              order.order_id,
                              "preparing",
                            )
                          }
                          className="rounded-lg bg-[#C5A059] px-4 py-2 text-xs font-black uppercase tracking-[.08em] text-[#191713] disabled:opacity-50"
                        >
                          Start preparing
                        </button>
                      )}

                      {order.status ===
                        "preparing" && (
                        <button
                          type="button"
                          disabled={
                            updatingOrderId ===
                            order.order_id
                          }
                          onClick={() =>
                            void updateOrderStatus(
                              order.order_id,
                              "ready",
                            )
                          }
                          className="rounded-lg bg-emerald-500 px-4 py-2 text-xs font-black uppercase tracking-[.08em] text-white disabled:opacity-50"
                        >
                          Mark ready
                        </button>
                      )}

                      {order.status ===
                        "ready" && (
                        <button
                          type="button"
                          disabled={
                            updatingOrderId ===
                            order.order_id
                          }
                          onClick={() =>
                            void updateOrderStatus(
                              order.order_id,
                              "completed",
                            )
                          }
                          className="rounded-lg bg-white px-4 py-2 text-xs font-black uppercase tracking-[.08em] text-[#191713] disabled:opacity-50"
                        >
                          Complete order
                        </button>
                      )}

                      {[
                        "pending",
                        "accepted",
                        "preparing",
                        "ready",
                      ].includes(
                        order.status,
                      ) && (
                        <button
                          type="button"
                          disabled={
                            updatingOrderId ===
                            order.order_id
                          }
                          onClick={() => {
                            const confirmed =
                              window.confirm(
                                `Cancel Order #${order.order_number}?`,
                              );

                            if (
                              confirmed
                            ) {
                              void updateOrderStatus(
                                order.order_id,
                                "cancelled",
                              );
                            }
                          }}
                          className="rounded-lg border border-red-400/30 px-4 py-2 text-xs font-black uppercase tracking-[.08em] text-red-300 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}

                      {updatingOrderId ===
                        order.order_id && (
                        <span className="flex items-center px-2 text-xs text-[#AFA695]">
                          Updating…
                        </span>
                      )}
                    </div>
                  )}

                <div className="mt-5 border-t border-white/10 pt-4">
                  {orderItems.length === 0 ? (
                    <p className="text-sm text-[#8E877B]">
                      No projected line items.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {orderItems.map((item) => (
                        <div
                          key={item.order_item_id}
                          className="flex justify-between gap-4 text-sm"
                        >
                          <span>
                            <strong>{item.quantity}×</strong>{" "}
                            {item.name}
                          </span>

                          <span className="whitespace-nowrap text-[#CFC5B2]">
                            {money(item.line_total_minor)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
